import { v4 as uuidv4 } from "uuid";

/**
 * Correlation-ID handling for outgoing HTTP requests.
 *
 * The codebase already stamps several IDs:
 *   - x-request-id            (per-request unique — retries get a new one)
 *   - X-Upload-Id             (logical package id, stable across retries)
 *   - X-Upload-Session-Id     (browser-scoped session id, from uploadSessionId.js)
 *
 * This module adds one more:
 *   - X-Correlation-Id        (equals X-Upload-Id when uploadId is known;
 *                              otherwise a fresh UUID)
 *
 * The header is a superset — for upload calls it is redundant with X-Upload-Id
 * but for the rest of the app (auth, metadata, admin) it gives backend logs a
 * single field they can grep across every request without needing to know
 * which specific ID a given endpoint uses. That predictability is the value.
 */
export const CORRELATION_HEADER = "X-Correlation-Id";

/**
 * Given an axios request config, decide what X-Correlation-Id it should carry.
 * Preference order:
 *   1. Existing X-Correlation-Id (idempotent — never overwrite)
 *   2. Existing X-Upload-Id       (upload calls share the correlation id)
 *   3. Fresh UUID
 *
 * @param {import('axios').InternalAxiosRequestConfig} config
 * @returns {string}
 */
export const deriveCorrelationId = (config) => {
  const existing = readHeader(config, CORRELATION_HEADER);
  if (existing) return existing;
  const uploadId = readHeader(config, "X-Upload-Id");
  if (uploadId) return uploadId;
  return uuidv4();
};

const readHeader = (config, name) => {
  if (!config?.headers) return null;
  const headers = config.headers;
  // axios 1.x uses an AxiosHeaders class with get(); we also handle plain
  // object headers set by legacy call sites.
  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase()) || null;
  }
  return headers[name] || headers[name.toLowerCase()] || null;
};
